// Copyright (c) StrangeBee 2023

import { SharingEventList } from '@Organisms/Shares/EntitySharingList/EntitySharingList';
import {
    CaseCustomField,
    CaseInput,
    CaseOutput,
    CaseController,
    AttachmentCaseType,
    ImportCaseOutput,
    FilterInput,
    SharedOrgOutput,
    UserOutput,
    PageInput,
    InputShare,
    BulkCaseTemplateParams,
    CaseOwnerChangeInput,
    CaseReportCreateInput,
    CaseReportFormatOutput,
    CaseReportAttachmentOutput,
    CaseReportRenderInput,
} from 'thehive-sdk';
import { convertCustomFields } from '@Helpers/customField';
import client, { queryClient } from '@Client';
import { computeGetSelector } from './selector';
import { openMessage } from '@Helpers/notification';

export interface CaseEventList {
    onGetDetails: (caseId: string) => Promise<CaseOutput>;
    onCreate: (caseData: CaseInput) => Promise<CaseOutput>;
    onEdit: (caseData: Partial<CaseInput>, caseId: string) => Promise<void | CaseOutput>;
    onDelete: (caseId: string) => Promise<void>;
    onBulkEdit: (caseData: Partial<CaseInput>, caseIds: string[]) => Promise<void | CaseOutput>;
    onBulkCaseTemplate: (data: Partial<BulkCaseTemplateParams>, caseIds: string[]) => Promise<void | any>;
    onMultipleEdit: (caseData: Partial<CaseInput>, caseIds: string[]) => void;
    onMultipleDelete: (casesIds: string[]) => Promise<void | CaseOutput>;
    onListAssignableUsers: (caseId: string) => Promise<UserOutput[]>;
    getExportUrl: (caseId: string, password: string) => string;
    onImport: (formData: FormData) => Promise<ImportCaseOutput>;
    onMISPImport: (formData: FormData) => Promise<any>;
    onOwnerChange: (options: CaseOwnerChangeInput, caseId: string) => Promise<void>;
    shares: SharingEventList;
    attachment: {
        onAdd: (
            caseId: string,
            attachments: FormData,
            refetch?: boolean,
        ) => Promise<{ attachments: AttachmentCaseType[] }>;
        onDelete: (caseId: string, attachmentId: string) => Promise<void>;
        onDownload: (caseId: string, attachmentId: string) => Promise<Blob>;
        getDownload: (params: Record<string, string>) => string;
    };
    pages: {
        onAdd: (page: PageInput, caseId: string) => void;
        onEdit: (page: Partial<PageInput>, caseId: string, pageId: string) => void;
        onDelete: (caseId: string, pageId: string) => void;
    };
    onCreateReport: (data: CaseReportCreateInput, caseId: string) => Promise<CaseReportAttachmentOutput | void>;
    onDeleteReport: (reportId: string) => Promise<void>;
    onUploadReport: (formData: FormData, caseId: string) => Promise<CaseReportAttachmentOutput | void>;
    onListReportFormat: () => Promise<CaseReportFormatOutput>;
    onRenderReport: (data: CaseReportRenderInput) => Promise<string>;
}

interface CaseEventListParams {
    locale: (label: string) => string;
    controller: CaseController;
    onSuccess?: (count?: boolean, message?: string) => void;
    onError?: (label?: string) => void;
}

export const computeEventList = ({ controller, locale, onError, onSuccess }: CaseEventListParams): CaseEventList => ({
    onGetDetails: (caseId: string): Promise<CaseOutput> =>
        controller.one({
            selector: computeGetSelector({ caseId }),
            paginationOptions: { page: 0, itemsPerPage: 1 },
        }),
    onCreate: (caseData: CaseInput) =>
        controller.create<CaseInput, CaseOutput>({
            ...caseData,
            customFields: convertCustomFields(caseData.customFields as CaseCustomField[]),
        }),
    onEdit: (caseData: Partial<CaseInput>, caseId: string): Promise<void | CaseOutput> =>
        controller
            .edit<Partial<CaseInput>, void>(
                Object.entries(caseData).reduce(
                    (acc, [key, value]) =>
                        key === 'customFields'
                            ? { ...acc, customFields: convertCustomFields(value as CaseCustomField[]) }
                            : { ...acc, [key]: value },
                    {},
                ),
                caseId,
            )
            .then((data) => {
                onSuccess?.(false, locale('notification.case.edit'));
                return data;
            })
            .catch(onError),
    onDelete: (caseId) =>
        controller
            .delete(caseId)
            .then(() => openMessage('success', locale('notification.case.delete')))
            .catch(onError),
    onBulkEdit: (caseData: Partial<CaseInput>, caseIds: string[]): Promise<void | CaseOutput> =>
        controller
            .bulkEdit<Partial<CaseInput>, void>(caseData, caseIds)
            .then((data) => {
                onSuccess?.(false, locale('notification.case.edit'));
                return data;
            })
            .catch(onError),
    onBulkCaseTemplate: (data: Partial<BulkCaseTemplateParams>, caseIds: string[]): Promise<void | any> =>
        controller
            .bulkCaseTemplate(data, caseIds)
            .then((data) => {
                onSuccess?.(false, locale('notification.case.edit'));
                return data;
            })
            .catch(onError),
    onMultipleEdit: (caseData: Partial<CaseInput>, caseIds: string[]): void => {
        controller
            .multipleEdit<Partial<CaseInput>, void>(caseData, caseIds)
            .then(() => onSuccess?.(false, locale('notification.case.edit')))
            .catch(onError);
    },
    onMultipleDelete: (casesIds: string[]): Promise<void | CaseOutput> => {
        return controller
            .multipleDelete(casesIds)
            .then(() => onSuccess?.(true, locale('notification.case.delete')))
            .catch(onError);
    },

    getExportUrl: (caseId, password): string => client.apiUrl + controller.getExportUrl(caseId, password),
    onImport: (formData: FormData): Promise<ImportCaseOutput> => controller.import(formData),
    onMISPImport: (formData: FormData): Promise<ImportCaseOutput> => client.MispController.import(formData),

    onOwnerChange: (options, caseId) =>
        controller
            .changeOwner(options, caseId)
            .then(() => onSuccess?.(false, locale('notification.case.owner-change')))
            .catch(onError),

    onListAssignableUsers: (caseId: string): Promise<UserOutput[]> =>
        controller.list<UserOutput[]>({
            selector: {
                head: [{ name: 'getCase', idOrName: caseId }, { name: 'assignableUsers' }],
                name: 'get-case-assignable-users',
            },
        }),
    attachment: {
        onAdd: (caseId, attachments, refetch = true) =>
            controller
                .createAttachments(attachments, caseId)
                .then((attachments) => {
                    if (refetch) {
                        onSuccess?.(true, locale('attachment.action.success.add'));
                    }
                    return attachments;
                })
                .catch((error) => {
                    onError?.(error);
                    return { attachments: [] };
                }),
        onDelete: (caseId, attachmentId): Promise<void> => controller.deleteAttachment(attachmentId, caseId),
        onDownload: (caseId, attachmentId): Promise<Blob> => controller.downloadAttachment(attachmentId, caseId),
        getDownload: (params: Record<string, string>): string =>
            window.location.origin + client.apiUrl + controller.getUrlDownloadAttachment(params),
    },
    shares: {
        onList: (caseId: string, filters?: FilterInput[]): Promise<SharedOrgOutput[]> =>
            controller.list({
                selector: {
                    head: [{ name: 'getCase', idOrName: caseId }, { name: 'shares' }],
                    initialFilters: filters,
                    name: `get-case-sharings-${caseId}`,
                },
            }),
        onAdd: (shares, caseId: string): void => {
            controller
                .addShare(shares as InputShare[], caseId)
                .then(() => {
                    onSuccess?.(true, locale('notification.case.sharing.add'));
                    queryClient.refetchQueries(['sharings', caseId]);
                })
                .catch(onError);
        },
        onBulkEdit: (shares, caseId: string): void => {
            controller
                .bulkEditShare(shares as InputShare[], caseId)
                .then(() => {
                    onSuccess?.(true, locale('notification.case.sharing.edit'));
                    queryClient.refetchQueries(['sharings', caseId]);
                })
                .catch(onError);
        },
        onEdit: (profile, shareId): Promise<void> =>
            controller
                .editShare(profile, shareId)
                .then(() => onSuccess?.())
                .catch(onError),
        onDelete: (organisations, caseId): void => {
            controller
                .deleteShare(organisations, caseId)
                .then(() => {
                    onSuccess?.(true, locale('notification.case.sharing.remove'));
                    queryClient.refetchQueries(['sharings']);
                })
                .catch(onError);
        },
    },
    pages: {
        onAdd: (page, caseId) => {
            controller
                .addPage(page, caseId)
                .then(() => onSuccess?.(true, locale('notification.case.pages.add')))
                .catch(onError);
        },
        onEdit: (page, caseId, pageId) => {
            controller
                .editPage(page, caseId, pageId)
                .then(() => onSuccess?.(true, locale('notification.case.pages.edit')))
                .catch(onError);
        },
        onDelete: (caseId, pageId) => {
            controller
                .deletePage(caseId, pageId)
                .then(() => onSuccess?.(true, locale('notification.case.pages.delete')))
                .catch(onError);
        },
    },
    onCreateReport: (data, caseId) =>
        controller
            .createReport(data, caseId)
            .then((data) => {
                onSuccess?.(true, locale('report.create.success'));
                return data;
            })
            .catch(onError),
    onDeleteReport: (reportId) => controller.deleteReport(reportId).then(() => onSuccess?.(true)),
    onUploadReport: (formData, caseId) =>
        controller
            .uploadReport(formData, caseId)
            .then((data) => {
                onSuccess?.(true, locale('attachment.action.success.add'));
                return data;
            })
            .catch(onError),
    onListReportFormat: () => controller.listReportFormat(),
    onRenderReport: (data: CaseReportRenderInput) =>
        controller.renderReport(data).then((data) => {
            onSuccess?.(true);
            return data;
        }),
});
